import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { calculateCosts } from "../../services/apiService";
import { formatCurrency } from "../../utils/formatters";
import { PRICING_TEMPLATE_FIELDS } from "../../utils/pricingTemplates";
import {
  REFERENCE_RATE_FIELDS,
  buildReferencePayload,
  rateConstantsDiffer,
} from "./referenceQuote";

/**
 * "What does this rate change actually do to a quote?"
 *
 * Both columns are real `/api/calculate` runs on the same frozen reference job
 * (see referenceQuote.js) — one with the rates as they are saved today, one
 * with the rates the admin just typed. Nothing here derives a pricing figure;
 * every dollar shown comes back from the calculator.
 *
 * Tone is direction, not judgement: a number that goes up is toned --wb-warn,
 * one that goes down --wb-good. Neither is "better" — profit going up and a
 * quote going up are the same arrow.
 */

/** Expected to be identical between the two runs — a moved build cost means a
 *  labor rate changed, which is legitimate but worth naming out loud. */
const BASELINE_ROW = { key: "total_job_costs_P91", label: "Build cost" };

const IMPACT_ROWS = [
  { key: "overhead_P93", label: "Overhead" },
  { key: "breakeven_P95", label: "Cost to break even" },
  { key: "profit_P97", label: "Profit" },
  { key: "sales_tax_P99", label: "Sales tax" },
  { key: "commissions_P103", label: "Commissions" },
  {
    key: "final_suggested_sale_price_P105",
    label: "Quote",
    emphasis: true,
  },
];

/** Percent constants carry their tier label; labor rates are $/hr. */
const PERCENT_LABELS = PRICING_TEMPLATE_FIELDS.reduce((map, field) => {
  map[field.key] = field.label.replace(/\s*\(%\)\s*$/, "");
  return map;
}, {});

const LABOR_LABELS = {
  labor_rate_type: "Default labor rate (retired)",
  labor_burden_rate: "Shop labor rate",
  island_millwork_labor_rate: "Island Millwork charge-out (reference)",
  labor_custom_rate: "Custom labor rate (retired)",
};

const LABOR_RATE_TYPE_LABELS = {
  burden: "Shop labor (burden)",
  island: "Island Millwork",
  custom: "Custom",
};

function toNumber(value) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function money(value) {
  const parsed = toNumber(value);
  return parsed === null ? "—" : formatCurrency(parsed);
}

/** "+$412.55" / "−$18.00" / "$0.00" — never a percentage. */
function formatDelta(before, after) {
  const a = toNumber(before);
  const b = toNumber(after);
  if (a === null || b === null) return { text: "—", direction: "flat" };
  const delta = b - a;
  const rounded = Math.round(delta * 100) / 100;
  if (rounded === 0) return { text: formatCurrency(0), direction: "flat" };
  return {
    text: `${rounded > 0 ? "+" : "−"}${formatCurrency(Math.abs(rounded))}`,
    direction: rounded > 0 ? "up" : "down",
  };
}

function describeConstant(field, value) {
  if (field === "labor_rate_type") {
    return LABOR_RATE_TYPE_LABELS[value] || value || "—";
  }
  const parsed = toNumber(value);
  if (parsed === null) return "—";
  if (PERCENT_LABELS[field]) return `${parsed}%`;
  return `$${parsed}/hr`;
}

/** The changed constants, spelled out in the admin's own labels. */
export function changedConstantList(before = {}, after = {}) {
  return REFERENCE_RATE_FIELDS.filter((field) => {
    if (field === "labor_rate_type") {
      return (before[field] || "") !== (after[field] || "");
    }
    return toNumber(before[field]) !== toNumber(after[field]);
  }).map((field) => ({
    field,
    label: PERCENT_LABELS[field] || LABOR_LABELS[field] || field,
    from: describeConstant(field, before[field]),
    to: describeConstant(field, after[field]),
  }));
}

/* ─────────────────────────────────────────────────────────────────────────
   The comparison itself. Shared by the save-gate dialog and the ambient
   inspector pane so both always say exactly the same thing.
   ───────────────────────────────────────────────────────────────────────── */

function ImpactRow({ label, before, after, emphasis }) {
  const delta = formatDelta(before, after);
  return (
    <tr
      className={`pi-row${emphasis ? " pi-row--emphasis" : ""}`}
      data-direction={delta.direction}
    >
      <th scope="row" className="pi-row__label">
        {label}
      </th>
      <td className="pi-row__num">{money(before)}</td>
      <td className="pi-row__num">{money(after)}</td>
      <td className="pi-row__num pi-row__delta">{delta.text}</td>
    </tr>
  );
}

export function PricingImpactTable({ before, after }) {
  const baselineMoved =
    formatDelta(before?.[BASELINE_ROW.key], after?.[BASELINE_ROW.key])
      .direction !== "flat";

  return (
    <div className="pi-table-wrap">
      <table className="pi-table">
        <thead>
          <tr>
            <th scope="col">Step</th>
            <th scope="col">Now</th>
            <th scope="col">After saving</th>
            <th scope="col">Change</th>
          </tr>
        </thead>
        <tbody>
          <ImpactRow
            label={BASELINE_ROW.label}
            before={before?.[BASELINE_ROW.key]}
            after={after?.[BASELINE_ROW.key]}
          />
          {IMPACT_ROWS.map((row) => (
            <ImpactRow
              key={row.key}
              label={row.label}
              before={before?.[row.key]}
              after={after?.[row.key]}
              emphasis={row.emphasis}
            />
          ))}
        </tbody>
      </table>

      {baselineMoved ? (
        <p className="pi-alert" role="status">
          Build cost moved. That means a labor rate changed, not just a
          percentage — the whole job is being built at a different cost.
        </p>
      ) : null}

      <p className="pi-note">
        Both columns are real calculator runs on the same fixed reference job.
        The only thing that changed between them is the rates you edited.
      </p>
    </div>
  );
}

export function ChangedConstantsList({ before, after }) {
  const changes = useMemo(
    () => changedConstantList(before, after),
    [before, after],
  );
  if (changes.length === 0) return null;
  return (
    <ul className="pi-changes">
      {changes.map((change) => (
        <li key={change.field}>
          <span className="pi-changes__label">{change.label}</span>{" "}
          <span className="pi-changes__from">{change.from}</span>
          <span className="pi-changes__arrow"> → </span>
          <span className="pi-changes__to">{change.to}</span>
        </li>
      ))}
    </ul>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Running the two calculations.
   ───────────────────────────────────────────────────────────────────────── */

/**
 * Fires both reference runs in parallel. `delay` debounces the ambient
 * inspector; the save gate passes 0 so the dialog starts working immediately.
 */
export function usePricingImpact({
  oldConstants,
  newConstants,
  active = true,
  delay = 0,
}) {
  const [state, setState] = useState({
    loading: false,
    error: null,
    before: null,
    after: null,
  });
  const runIdRef = useRef(0);

  const oldKey = JSON.stringify(oldConstants || {});
  const newKey = JSON.stringify(newConstants || {});

  const run = useCallback(() => {
    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    setState((prev) => ({ ...prev, loading: true, error: null }));

    return Promise.all([
      calculateCosts(buildReferencePayload(JSON.parse(oldKey))),
      calculateCosts(buildReferencePayload(JSON.parse(newKey))),
    ])
      .then(([before, after]) => {
        if (runIdRef.current !== runId) return;
        setState({ loading: false, error: null, before, after });
      })
      .catch((error) => {
        if (runIdRef.current !== runId) return;
        setState({
          loading: false,
          error:
            error?.error || error?.message || "the calculator did not respond.",
          before: null,
          after: null,
        });
      });
  }, [oldKey, newKey]);

  useEffect(() => {
    if (!active) return undefined;
    if (delay <= 0) {
      run();
      return undefined;
    }
    const timer = setTimeout(run, delay);
    return () => clearTimeout(timer);
  }, [active, delay, run]);

  // Abandon any in-flight run when the component goes away.
  useEffect(
    () => () => {
      runIdRef.current += 1;
    },
    [],
  );

  return { ...state, run };
}

/* ─────────────────────────────────────────────────────────────────────────
   The save gate.
   ───────────────────────────────────────────────────────────────────────── */

function PricingImpactDialog({
  open,
  oldConstants,
  newConstants,
  saving = false,
  onConfirm,
  onCancel,
}) {
  const { loading, error, before, after } = usePricingImpact({
    oldConstants,
    newConstants,
    active: open,
    delay: 0,
  });

  const ready = Boolean(before && after) && !loading && !error;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // While the two runs are in flight a stray outside click must not
        // dismiss the gate. Escape is handled by Radix and left alone.
        if (!next && !loading) onCancel();
      }}
    >
      <DialogContent
        className="pi-dialog"
        onInteractOutside={(event) => {
          if (loading) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Before you save these rates</DialogTitle>
          <DialogDescription>
            The same reference job, priced with today's rates and with yours.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="pi-status" role="status">
            Pricing the reference job both ways…
          </p>
        ) : null}

        {error ? (
          <p className="pi-status pi-status--error" role="alert">
            Could not run the comparison — {error}
          </p>
        ) : null}

        {ready ? (
          <>
            <PricingImpactTable before={before} after={after} />
            <div className="pi-changes-block">
              <h4 className="pi-changes__hd">What you changed</h4>
              <ChangedConstantsList
                before={oldConstants}
                after={newConstants}
              />
            </div>
          </>
        ) : null}

        <div className="pi-actions">
          <button
            type="button"
            className="wb-plainbtn pi-btn"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="wb-plainbtn pi-btn pi-btn--primary"
            onClick={onConfirm}
            disabled={saving || loading}
          >
            {saving ? "Saving…" : error ? "Save anyway" : "Save these rates"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { rateConstantsDiffer };
export default PricingImpactDialog;
